import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  Clock as ClockIcon,
  Trash2,
  CheckCheck,
  Settings,
  Filter,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
  Star,
  ShoppingBag,
  User,
  Mail,
  BellOff,
  AlertTriangle,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { notificationService } from "../../api/services/notificationService";
import { useToast } from "../../components/common/ThemeToaster";

const UserNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    read: 0,
    archived: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const navigate = useNavigate();
  const {
    success: toastSuccess,
    error: toastError,
    info: toastInfo,
  } = useToast();

  useEffect(() => {
    fetchNotifications();
  }, [typeFilter, statusFilter, sortBy, sortOrder, pagination.page]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      // Map status filter
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      // Map type filter
      if (typeFilter !== "all") {
        params.type = typeFilter;
      }

      console.log("Fetching notifications with params:", params);
      const response = await notificationService.getUserNotifications(params);
      console.log("Notifications response:", response);

      let notificationsData = [];
      let paginationData = null;

      if (
        response?.data?.notifications &&
        Array.isArray(response.data.notifications)
      ) {
        notificationsData = response.data.notifications;
        paginationData = response.data.pagination;
      } else if (
        response?.notifications &&
        Array.isArray(response.notifications)
      ) {
        notificationsData = response.notifications;
        paginationData = response.pagination;
      } else if (Array.isArray(response?.data)) {
        notificationsData = response.data;
      } else if (Array.isArray(response)) {
        notificationsData = response;
      }

      setNotifications(notificationsData);

      if (paginationData) {
        setPagination((prev) => ({
          ...prev,
          total: paginationData.total || notificationsData.length,
          totalPages: paginationData.totalPages || 1,
        }));
      }

      // Fetch stats
      await fetchStats();
    } catch (err) {
      console.error("Error fetching notifications:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Failed to load notifications";
      setError(errorMsg);
      toastError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      const unreadCount = response?.data?.unreadCount || 0;

      setStats({
        total: notifications.length,
        unread: unreadCount,
        read: notifications.filter((n) => n.status === "read").length,
        archived: notifications.filter((n) => n.status === "archived").length,
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const markAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === id
            ? { ...notif, status: "read", readAt: new Date().toISOString() }
            : notif,
        ),
      );
      toastSuccess("Notification marked as read");
      fetchStats();
    } catch (err) {
      console.error("Error marking as read:", err);
      toastError("Failed to mark as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) =>
        prev.map((notif) => ({
          ...notif,
          status: "read",
          readAt: new Date().toISOString(),
        })),
      );
      setSelectedNotifications(new Set());
      setSelectAll(false);
      toastSuccess("All notifications marked as read");
      fetchStats();
    } catch (err) {
      console.error("Error marking all as read:", err);
      toastError("Failed to mark all as read");
    }
  };

  const archiveNotification = async (id) => {
    try {
      await notificationService.markAsArchived(id);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === id ? { ...notif, status: "archived" } : notif,
        ),
      );
      toastSuccess("Notification archived");
      fetchStats();
    } catch (err) {
      console.error("Error archiving notification:", err);
      toastError("Failed to archive notification");
    }
  };

  const deleteNotification = async (id) => {
    try {
      await notificationService.deleteNotification(id);
      setNotifications((prev) => prev.filter((notif) => notif.id !== id));
      setSelectedNotifications((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      toastSuccess("Notification deleted");
      fetchStats();
    } catch (err) {
      console.error("Error deleting notification:", err);
      toastError("Failed to delete notification");
    }
  };

  const deleteSelected = async () => {
    if (selectedNotifications.size === 0) return;

    try {
      const ids = Array.from(selectedNotifications);
      await notificationService.bulkDeleteNotifications(ids);
      setNotifications((prev) =>
        prev.filter((notif) => !selectedNotifications.has(notif.id)),
      );
      setSelectedNotifications(new Set());
      setSelectAll(false);
      toastSuccess(`${ids.length} notification(s) deleted`);
      fetchStats();
    } catch (err) {
      console.error("Error deleting selected:", err);
      toastError("Failed to delete notifications");
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(filteredNotifications.map((n) => n.id)));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectNotification = (id) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedNotifications(newSelected);
    setSelectAll(newSelected.size === filteredNotifications.length);
  };

  const getNotificationTypeColor = (type) => {
    const colors = {
      info: "bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400",
      success:
        "bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400",
      warning:
        "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400",
      error: "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400",
      promotion:
        "bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400",
      order:
        "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400",
      account:
        "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/20 dark:text-cyan-400",
      system: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
    };
    return colors[type] || colors.info;
  };

  const getNotificationTypeIcon = (type) => {
    const icons = {
      info: Info,
      success: CheckCircle,
      warning: AlertTriangle,
      error: XCircle,
      promotion: Star,
      order: ShoppingBag,
      account: User,
      system: Settings,
    };
    return icons[type] || Bell;
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        return formatDistanceToNow(date, { addSuffix: true });
      }
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (typeFilter !== "all" && notification.type !== typeFilter) return false;
    if (statusFilter !== "all" && notification.status !== statusFilter)
      return false;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        notification.title.toLowerCase().includes(searchLower) ||
        notification.message.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    let comparison = 0;
    if (sortBy === "createdAt") {
      comparison = new Date(b.createdAt) - new Date(a.createdAt);
    } else if (sortBy === "title") {
      comparison = a.title.localeCompare(b.title);
    } else if (sortBy === "type") {
      comparison = a.type.localeCompare(b.type);
    }
    return sortOrder === "DESC" ? comparison : -comparison;
  });

  const unreadCount = notifications.filter((n) => n.status === "unread").length;

  const notificationTypes = [
    { value: "all", label: "All", icon: Bell },
    { value: "info", label: "Info", icon: Info },
    { value: "success", label: "Success", icon: CheckCircle },
    { value: "warning", label: "Warning", icon: AlertTriangle },
    { value: "error", label: "Error", icon: XCircle },
    { value: "promotion", label: "Promotion", icon: Star },
    { value: "order", label: "Order", icon: ShoppingBag },
    { value: "account", label: "Account", icon: User },
    { value: "system", label: "System", icon: Settings },
  ];

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <motion.div
      whileHover={{ y: -2, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all"
    >
      <div className="flex items-center justify-between">
        <div
          className={`p-2 rounded-xl bg-${color}-100 dark:bg-${color}-900/20`}
        >
          <Icon
            size={20}
            className={`text-${color}-600 dark:text-${color}-400`}
          />
        </div>
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value}
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{label}</p>
    </motion.div>
  );

  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="relative">
            <Loader2
              size={48}
              className="animate-spin text-primary-500 mx-auto mb-4"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Bell size={24} className="text-primary-300" />
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium">
            Loading notifications...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span className="px-2.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Stay updated with your latest activities
          </p>
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors flex items-center gap-2"
            >
              <CheckCheck size={18} />
              Mark all as read
            </button>
          )}
          <button
            onClick={() => setShowSettingsModal(true)}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <Settings size={18} />
            Settings
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Bell}
            label="Total"
            value={stats.total}
            color="blue"
          />
          <StatCard
            icon={Mail}
            label="Unread"
            value={stats.unread}
            color="red"
          />
          <StatCard
            icon={CheckCircle}
            label="Read"
            value={stats.read}
            color="green"
          />
          <StatCard
            icon={Archive}
            label="Archived"
            value={stats.archived}
            color="gray"
          />
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 ${
                showFilters
                  ? "bg-primary-50 border-primary-200 text-primary-600 dark:bg-primary-900/20 dark:border-primary-800"
                  : "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100"
              }`}
            >
              <Filter size={18} />
              Filters
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="createdAt">Date</option>
              <option value="title">Title</option>
              <option value="type">Type</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC")}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-100 transition-colors"
            >
              {sortOrder === "ASC" ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 mt-4 border-t border-gray-100 dark:border-gray-800">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Type
                  </label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {notificationTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All</option>
                    <option value="unread">Unread</option>
                    <option value="read">Read</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setTypeFilter("all");
                      setStatusFilter("all");
                      setSearchTerm("");
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 font-medium"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bulk Actions */}
      <AnimatePresence>
        {selectedNotifications.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-800 rounded-2xl p-4 flex items-center justify-between shadow-sm"
          >
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
              {selectedNotifications.size} notification
              {selectedNotifications.size !== 1 ? "s" : ""} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  selectedNotifications.forEach((id) => markAsRead(id));
                  setSelectedNotifications(new Set());
                  setSelectAll(false);
                }}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
              >
                <CheckCheck size={14} />
                Mark Read
              </button>
              <button
                onClick={deleteSelected}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl flex items-center gap-3"
          >
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700 dark:text-red-300 text-sm flex-1">
              {error}
            </p>
            <button
              onClick={() => setError(null)}
              className="p-1 hover:bg-red-100 rounded-lg"
            >
              <X size={16} className="text-red-500" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notifications List */}
      <div className="space-y-3">
        <AnimatePresence>
          {sortedNotifications.map((notification) => {
            const Icon = getNotificationTypeIcon(notification.type);
            const colorClass = getNotificationTypeColor(notification.type);
            const isUnread = notification.status === "unread";

            return (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className={`bg-white dark:bg-gray-900 rounded-2xl border ${
                  isUnread
                    ? "border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50/30 to-white dark:from-primary-900/10 dark:to-gray-900"
                    : "border-gray-200 dark:border-gray-800"
                } shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group`}
              >
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.has(notification.id)}
                        onChange={() =>
                          handleSelectNotification(notification.id)
                        }
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </div>

                    {/* Icon */}
                    <div
                      className={`flex-shrink-0 p-2 rounded-xl ${colorClass}`}
                    >
                      <Icon size={20} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3
                            className={`font-semibold mb-1 ${isUnread ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}
                          >
                            {notification.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {notification.message}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <ClockIcon size={12} />
                              {formatTime(notification.createdAt)}
                            </span>
                            <span className="flex items-center gap-1 capitalize">
                              <TagIcon size={12} />
                              {notification.type}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isUnread && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                              title="Mark as read"
                            >
                              <CheckCheck
                                size={14}
                                className="text-green-500"
                              />
                            </button>
                          )}
                          <button
                            onClick={() => archiveNotification(notification.id)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                            title="Archive"
                          >
                            <Archive size={14} className="text-gray-500" />
                          </button>
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={14} className="text-red-500" />
                          </button>
                        </div>
                      </div>

                      {/* Action Link */}
                      {notification.link && (
                        <button
                          onClick={() => navigate(notification.link)}
                          className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                        >
                          View Details
                          <ChevronUp size={14} className="rotate-90" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {sortedNotifications.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800"
        >
          <div className="inline-flex p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
            <BellOff size={48} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No notifications
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {searchTerm || typeFilter !== "all" || statusFilter !== "all"
              ? "No notifications match your filters. Try adjusting your search criteria."
              : "You're all caught up! New notifications will appear here."}
          </p>
        </motion.div>
      )}
    </div>
  );
};

// Tag Icon Component
const TagIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2H2v10l9.17 9.17a2 2 0 0 0 2.83 0l7-7a2 2 0 0 0 0-2.83L12 2z" />
    <path d="M7 7h.01" />
  </svg>
);

// Archive Icon Component
const Archive = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="20" height="5" x="2" y="3" rx="1" />
    <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
    <path d="M10 12h4" />
  </svg>
);

export default UserNotifications;
